import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useCurrentShopLocalesAvailables } from "@/lib/translator";
import useTextDtoValue from "@/utils/use-text-dto-value";
import { UpdateCustomMessageDialog } from "../custom-messages-settings/update-custom-message-dialog";
import { ExclamationTriangleIcon, GlobeIcon } from "@radix-ui/react-icons";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { SettingsFormHeader } from "@babackapp/react-hook-forms-components/form-page";
import { Separator } from "@radix-ui/react-select";
import AddReturnReasonDialog from "./add-return-reason-dialog.tsx";
import ReturnReasonListItem from "./return-reason-list-item.tsx";
import useTranslation from "@babackapp/translations/use-translation";
import VerticalDragAndDrop from "@/components-legacy/verticalDragAndDrop";
import type { ShopReasonsDto } from "@babackapp/private-api-client/types";
import { useParams } from "react-router-dom";
import useUpdateShopReasons from "@babackapp/private-api-client/client/use-update-shop-reasons";
import { useEffect, useState } from "react";

export default function ReturnReasonList() {
  const t = useTranslation();
  const shopId = useParams<{ shopId: string }>().shopId as string;
  const [activeReasons, setActiveReasons] = useState<ShopReasonsDto[]>([]);

  const { data: shop } = useCurrentShopSuspense();

  useEffect(() => {
    setActiveReasons(
      shop.reasons.filter(
        (reason) =>
          reason.active && reason.babackStandardReason !== "RETRACTION",
      ),
    );
  }, [shop]);

  const { mutate: updateReasons } = useUpdateShopReasons(shopId);

  const languages = useCurrentShopLocalesAvailables();
  const retractationLabel = shop.preferences.userPortal.retractationLabel ?? [];
  const retractationLabelText = useTextDtoValue(
    retractationLabel.length > 0 ? retractationLabel : null,
  );
  const retractationMissingLanguages =
    retractationLabel.length > 0
      ? languages?.filter((lang) => {
          const value = retractationLabel.find((v) => v.lang === lang.code);
          return !value || value.text.trim().length === 0;
        })
      : [];

  const handleReasonsReorder = (reorderedReasons: ShopReasonsDto[]) => {
    const otherReasons = shop.reasons.filter(
      (reason) =>
        !reason.active || reason.babackStandardReason === "RETRACTION",
    );
    const updatedReasons = [...reorderedReasons, ...otherReasons];
    setActiveReasons(reorderedReasons);
    updateReasons(updatedReasons);
  };

  return (
    <Card>
      <CardContent className="py-8">
        <div className="flex items-center justify-between">
          <SettingsFormHeader
            title={t("settings.reasons.title")}
            description={t("settings.reasons.subtitle")}
          />
          <AddReturnReasonDialog>
            <Button>{t("misc.add")}</Button>
          </AddReturnReasonDialog>
          <Separator className="mt-4" />
        </div>
        <div className="flex flex-col mt-2">
          <VerticalDragAndDrop<ShopReasonsDto>
            items={activeReasons}
            onChange={handleReasonsReorder}
            itemRenderer={(reason) => <ReturnReasonListItem reason={reason} />}
          />
          {shop.preferences.retractationEnabled && (
            <div className="border-t border-border">
              <UpdateCustomMessageDialog customKey="retractationLabel" plain>
                <button
                  role="button"
                  className="flex flex-row py-2 justify-start items-center relative group h-auto px-2 my-2 rounded-lg bg-transparent hover:bg-muted whitespace-normal w-full gap-4"
                >
                  {retractationMissingLanguages?.length ? (
                    <ExclamationTriangleIcon className="size-4 text-destructive" />
                  ) : (
                    <GlobeIcon className="size-4 text-muted-foreground" />
                  )}
                  <div className="flex h-full text-left flex-col items-start w-full justify-center py-2">
                    <p className="text-base font-medium leading-none tracking-tight text-left">
                      {retractationLabel.length > 0
                        ? retractationLabelText
                        : t(
                            "settings.customerPortal.basics.fields.retractationEnabled.title",
                          )}
                    </p>
                    {retractationMissingLanguages?.length ? (
                      <span className="inline-flex gap-2 flex-wrap mt-2">
                        {retractationMissingLanguages.map((lang) => (
                          <span
                            key={lang.code}
                            className="inline-flex items-center gap-1 rounded-full border border-destructive px-2 py-0.5 text-xs text-destructive"
                          >
                            <ExclamationTriangleIcon className="size-3" />
                            {t("settings.reasons.missingLanguage", {
                              language: lang.name,
                            })}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </div>
                </button>
              </UpdateCustomMessageDialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
